import { useCallback, useEffect, useId, useState } from 'react';
import PageState from '../../components/PageState.jsx';
import { api } from '../../lib/api.js';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';

// Plain sentences for the codes the API answers (portal/shared/sending-domain.mjs and routes/company-sending-domain.mjs).
const ERROR_TEXT = {
  invalid_domain: 'That is not a valid domain name.',
  not_subdomain: 'Use a dedicated subdomain such as alerts.yourcompany.com, not a domain you already use for mail.',
  primary_domain: 'Use a dedicated subdomain such as alerts.yourcompany.com, not your main mail domain.',
  parent_not_verified: 'The domain must be a subdomain of a domain your organization has verified in Microsoft Entra ID.',
  reserved_domain: 'That domain cannot be used as a sending domain.',
  public_provider: 'A free-mail or public provider domain cannot be used as a sending domain.',
  display_name_reserved: 'Neither the organization name nor the company name can be shown as the sender: they cannot contain IPC Global or a vendor name. Contact support@ipc-global.com to change the company name.',
  one_domain_per_tenant: 'This organization already has a sending domain. Remove it first.',
  domain_taken: 'That domain is already in use.',
  ses_unavailable: 'The mail service could not set up the domain. Try again in a few minutes.',
  rate_limited: 'That was tried too often. Try again later.',
  send_failed: 'The test message could not be sent from this sender.',
  'claim-race': 'The settings were changed by someone else. Reload and try again.',
  domains_unconfirmed: 'Your verified domains could not be confirmed with Microsoft right now. Try again in a few minutes.',
  still_over_threshold: 'Delivery quality is still too low. Try again after the recent bounces and complaints age out.',
  unavailable: 'Delivery quality could not be checked right now. Try again in a few minutes.',
};
// The 429 answers carry their own limit ("six per hour", "five per hour"); that sentence is the useful one.
const errorText = (e) => (e?.body?.error === 'rate_limited' && typeof e.body.message === 'string' && e.body.message ? e.body.message : null) ?? ERROR_TEXT[e?.body?.error] ?? ERROR_TEXT[e?.body?.details?.reason] ?? (typeof e?.message === 'string' && e.message && !/^HTTP \d+$/.test(e.message) ? e.message : 'That did not work. Try again in a few minutes.');

const STATE_LINE = {
  disabled: 'Not set up. Email leaves from the IPC Global sender.',
  'pending-dns': 'Waiting for DNS. Email leaves from the IPC Global sender until it is verified.',
  verified: 'Verified.',
  active: 'Verified and in use.',
  degraded: 'Needs attention. Email leaves from the IPC Global sender until it is fixed.',
  reverted: 'Switched back to the IPC Global sender because of delivery quality.',
  failed: 'Verification timed out. Email leaves from the IPC Global sender.',
};
// Why a verified domain still sends from the IPC Global sender (senderReason from the API).
const SENDER_REASON = {
  name_reserved: 'neither the organization name nor the company name can be shown as the sender, because they cannot contain IPC Global or a vendor name. Contact support@ipc-global.com to change the company name.',
  invalid_sender: 'the sender name could not be used in a mail header. Contact support@ipc-global.com to change the company name.',
};
// What "Check now" found, and what to do next, after the answer arrives.
const CHECK_RESULT = {
  active: 'verified and in use.',
  verified: 'verified.',
  'pending-dns': 'still waiting for DNS. New records can take up to 72 hours to appear. Check again later.',
  degraded: 'needs attention. Fix the records shown below, then check again.',
  reverted: 'switched back to the IPC Global sender because of delivery quality. Review your recipient lists, then confirm to verify again.',
  failed: 'the records were not found in time. Remove this domain and start over once the records exist.',
  disabled: 'no sending domain is set up.',
};
const checkResultLine = (out) => `Checked just now: ${CHECK_RESULT[out?.state] ?? 'done.'}`;
const testResultLine = (out) => {
  if (out?.sender === 'customer') return 'A test message was sent to you from your own domain.';
  const why = SENDER_REASON[out?.reason];
  return why
    ? `A test message was sent to you from the IPC Global sender. Your domain is not used for sending because ${why}`
    : 'A test message was sent to you from the IPC Global sender. Your domain is not in use yet.';
};

const whenText = (iso) => { const t = Date.parse(iso ?? ''); return Number.isFinite(t) ? `${new Date(t).toISOString().replace('T', ' ').slice(0, 16)} UTC` : 'Not yet'; };

function Spinner() {
  return (
    <svg className="mr-2 inline h-4 w-4 animate-spin align-[-2px] text-ipc-light" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

const CAUSE = {
  dkim_failed: 'The DKIM records could not be verified.',
  mailfrom_failed: 'The MAIL FROM records could not be verified.',
  temporary_failure: 'A temporary DNS problem.',
  records_missing: 'One or more DNS records are missing.',
  send_failed: 'A message from your domain was rejected.',
  dns_timeout: 'The records were not found within 72 hours.',
  reputation: 'Too many messages bounced or were reported as unwanted.',
};

function RecordsTable({ records }) {
  const [copied, setCopied] = useState('');
  async function copy(value, key) {
    try { await navigator.clipboard.writeText(value); setCopied(key); } catch { setCopied(''); }
  }
  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-slate-600"><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Name</th><th className="py-1 pr-3">Value</th><th className="py-1"><span className="sr-only">Copy</span></th></tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={`${r.purpose}-${i}`} className="border-b border-slate-100 align-top">
              <td className="py-1 pr-3 font-medium">{r.type}</td>
              <td className="py-1 pr-3 break-all font-mono">{r.name}</td>
              <td className="py-1 pr-3 break-all font-mono">{r.value}</td>
              <td className="py-1">
                <button type="button" className="underline" aria-label={`Copy the ${r.type} value for ${r.name}`} onClick={() => copy(r.value, `${r.purpose}-${i}`)}>
                  {copied === `${r.purpose}-${i}` ? 'Copied' : 'Copy'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Email (ENTRAID-340): the optional customer sending domain. onGuard never creates DNS records: the customer, or whoever
 * manages their DNS, creates the ones shown here. Admin only to change; an operator (and the operator's client view) reads the same details, read only.
 *
 * @param {object} props
 * @param {string} props.customerId
 * @param {boolean} props.readOnly an operator who is not this tenant's admin
 */
export default function EmailTab({ customerId, readOnly }) {
  const id = useId();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  // The start form is only shown while no domain is set up; typed text there is unsaved until Start succeeds.
  useUnsavedChanges(!readOnly && data?.state === 'disabled' && domain.trim() !== '');

  const load = useCallback(async () => {
    setLoadError('');
    try { setData(await api.sendingDomain(customerId)); } catch { setLoadError('The sending domain could not be loaded. Try again in a few minutes.'); }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  if (loadError) return <div className="card border-red-200 text-red-700" role="alert">{loadError}</div>;
  if (!data) return <PageState state="loading" header={false} cards={0} label="Loading the sending domain" />;

  async function run(kind, fn, after) {
    if (busy) return;
    setBusy(kind);
    setError('');
    setNote('');
    try {
      const out = await fn();
      if (after) after(out); else setData(out);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  }

  const state = data.state;
  const cause = data.errorClass ? CAUSE[data.errorClass] ?? null : null;
  const canStart = !readOnly && state === 'disabled';
  const optional = Array.isArray(data.optionalRecords) ? data.optionalRecords : [];
  const showRecords = (readOnly ? state !== 'disabled' : ['pending-dns', 'degraded'].includes(state)) && Array.isArray(data.records) && data.records.length > 0;
  const csvHref = data.csv ? `data:text/csv;charset=utf-8,${encodeURIComponent(data.csv)}` : null;

  return (
    <div className="space-y-6">
      <section className="card space-y-3" aria-labelledby={`${id}-heading`}>
        <h2 id={`${id}-heading`}>Sending domain</h2>
        <p className="text-sm text-slate-700">Today: {data.currentFrom}</p>
        <p role="status" className="text-sm text-slate-700">
          {state === 'active' && data.senderReason && SENDER_REASON[data.senderReason]
            ? `Verified, but mail still leaves from the IPC Global sender because ${SENDER_REASON[data.senderReason]}`
            : state === 'active' ? `Verified, sending from ${data.domain}` : STATE_LINE[state] ?? STATE_LINE.disabled}
          {state === 'pending-dns' && data.domain ? ` (${data.domain})` : ''}
        </p>
        {cause && state !== 'active' ? <p className="text-sm text-slate-700">{cause}</p> : null}

        {readOnly ? (
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm text-slate-700" data-testid="sending-domain-details">
            <dt className="font-medium">Domain</dt><dd>{data.domain ?? 'None'}</dd>
            {data.mailFromDomain ? <><dt className="font-medium">Return path</dt><dd>{data.mailFromDomain}</dd></> : null}
            <dt className="font-medium">Region</dt><dd>{data.region ?? 'Not set'}</dd>
            <dt className="font-medium">Created</dt><dd>{whenText(data.createdAt)}</dd>
            <dt className="font-medium">Last check</dt><dd>{whenText(data.lastCheckAt)}</dd>
            <dt className="font-medium">Verified on</dt><dd>{whenText(data.verifiedAt)}</dd>
          </dl>
        ) : null}

        {canStart ? (
          <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); run('start', () => api.startSendingDomain(customerId, domain.trim())); }}>
            <label htmlFor={`${id}-domain`} className="block text-sm font-medium">Sending domain</label>
            <input id={`${id}-domain`} className="w-full max-w-md rounded border border-slate-300 px-2 py-1 text-sm" value={domain} onChange={(e) => setDomain(e.target.value)} autoComplete="off" spellCheck={false} aria-describedby={`${id}-help`} />
            <p id={`${id}-help`} className="text-xs text-slate-500">Use a dedicated subdomain such as alerts.yourcompany.com. Do not use your main mail domain.</p>
            <button type="submit" className="btn-primary" disabled={busy !== '' || domain.trim() === ''}>Start</button>
          </form>
        ) : null}

        {showRecords ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-700">You (or whoever manages your DNS) create these records. onGuard never creates DNS records.</p>
            <RecordsTable records={data.records} />
            {csvHref ? <a className="text-sm underline" href={csvHref} download="onguard-dns-records.csv">Download CSV</a> : null}
            {optional.length > 0 ? (
              <div className="space-y-2 border-t border-slate-200 pt-3" data-testid="optional-dmarc">
                <h3 className="text-sm font-medium">Optional: a stricter DMARC policy</h3>
                <p className="text-xs text-slate-600">
                  This subdomain already follows the DMARC policy of your main domain, so you do not need a DMARC record. If you want a stricter
                  policy for this subdomain only, you may add the record below. It asks receivers to quarantine mail that fails DMARC. The DKIM
                  signature uses exactly this subdomain, so strict DKIM alignment holds; SPF alignment stays relaxed because the return path is
                  bounce.{data.domain}. It is not part of the CSV and onGuard does not check it.
                </p>
                <RecordsTable records={optional} />
              </div>
            ) : null}
          </div>
        ) : null}

        {state === 'reverted' ? (
          <p className="text-sm text-slate-700">Check your recipient lists, then confirm to verify the domain again.</p>
        ) : null}
        {state === 'failed' ? <p className="text-sm text-slate-700">The records were not found in time. Remove this domain and start over once the records exist.</p> : null}

        {readOnly ? null : <div className="flex flex-wrap gap-2">
          {['pending-dns', 'degraded', 'active'].includes(state) ? <button type="button" className="btn-secondary" disabled={busy !== ''} aria-busy={busy === 'check'} onClick={() => run('check', () => api.checkSendingDomain(customerId), (out) => { setData(out); setNote(checkResultLine(out)); })}>{busy === 'check' ? <><Spinner />Checking...</> : 'Check now'}</button> : null}
          {['pending-dns', 'degraded', 'active'].includes(state) ? (
            <button type="button" className="btn-secondary" disabled={busy !== ''} aria-busy={busy === 'test'} onClick={() => run('test', () => api.sendTestMessage(customerId), (out) => setNote(testResultLine(out)))}>{busy === 'test' ? <><Spinner />Sending...</> : 'Send me a test message'}</button>
          ) : null}
          {state === 'reverted' ? <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => run('ack', () => api.acknowledgeSendingDomain(customerId))}>I understand, check again</button> : null}
          {state === 'failed' ? <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => run('delete', () => api.disableSendingDomain(customerId))}>Start over</button> : null}
          {['pending-dns', 'degraded', 'active', 'reverted'].includes(state) ? <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => run('delete', () => api.disableSendingDomain(customerId))}>Remove the sending domain</button> : null}
        </div>}
        {data.cleanup === 'incomplete' ? <p className="text-xs text-slate-600">The domain is off. IPC Global will finish removing the mail setup.</p> : null}
        {readOnly ? <p className="text-xs text-slate-500">Read only. The customer's admin manages the sending domain.</p> : null}
        <p role="status" className="text-sm text-slate-700">{busy === 'check' ? 'Checking your domain with the mail service...' : busy === 'test' ? 'Sending the test message...' : note}</p>
        {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
      </section>
    </div>
  );
}
